import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_INFO = {
  'immobilienmarkt': { name: 'Immobilienmarkt', color: 'bg-blue-500' },
  'tips-tricks': { name: 'Tipps & Tricks', color: 'bg-emerald-500' },
  'bauprojekte': { name: 'Bauprojekte', color: 'bg-amber-500' },
  'news-trends': { name: 'News & Trends', color: 'bg-violet-500' }
};

const LazyBlogImage = ({ postId, hasImage, title, category }) => {
  const [imageData, setImageData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!hasImage || !postId) { setLoaded(true); return; }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetch(`${API_URL}/api/blog/posts/${postId}/image`)
            .then(r => r.ok ? r.json() : Promise.reject())
            .then(d => {
              // Ignore tiny placeholder images (1x1 pixel)
              if (d.image_base64 && d.image_base64.length > 200) {
                setImageData(d.image_base64);
              }
              setLoaded(true);
            })
            .catch(() => setLoaded(true));
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [postId, hasImage]);

  const catInfo = CATEGORY_INFO[category] || { color: 'bg-gray-400' };

  return (
    <div ref={imgRef} className="w-full h-full">
      {!loaded && (
        <div className={`w-full h-full ${catInfo.color}/20 animate-pulse`} />
      )}
      {loaded && imageData ? (
        <img
          src={`data:image/jpeg;base64,${imageData}`}
          alt={title}
          loading="lazy"
          width="400"
          height="260"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : loaded ? (
        <div className="w-full h-full bg-gradient-to-br from-[#264F77] to-[#1e3d5c] flex items-center justify-center">
          <div className="text-center">
            <span className="text-white/20 text-6xl font-light block" style={{ fontFamily: "'Playfair Display', serif" }}>LIVAG</span>
            <span className={`inline-block mt-2 px-3 py-1 rounded-full text-white/50 text-xs ${catInfo.color}/30`}>
              {CATEGORY_INFO[category]?.name || ''}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('de-CH', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

export const BlogPreview = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/blog/posts/public`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setPosts(data.slice(0, 3));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (!loading && posts.length === 0) return null;

  return (
    <section className="py-24 md:py-32 bg-[#f8f7f5]" data-testid="blog-preview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 md:mb-20">
          <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#264F77]/60 mb-4">
            Aktuelles von LIVAG
          </p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2
              className="text-4xl sm:text-5xl font-normal text-[#264F77]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Neueste Einblicke.
            </h2>
            <Link
              to="/blog"
              className="group inline-flex items-center text-[#264F77] font-semibold hover:text-[#1e3d5c] transition-colors self-start md:self-auto"
              data-testid="blog-view-all-link"
            >
              Alle Beiträge ansehen
              <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Blog Cards Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 rounded-xl h-56 mb-5" />
                <div className="h-3 bg-gray-200 rounded w-1/3 mb-4" />
                <div className="h-5 bg-gray-200 rounded w-4/5 mb-3" />
                <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {posts.map((post) => {
              const catInfo = CATEGORY_INFO[post.category] || { name: post.category, color: 'bg-gray-500' };
              return (
                <article
                  key={post.id}
                  className="group"
                  data-testid={`blog-preview-card-${post.slug}`}
                >
                  <Link to={`/blog/${post.slug}`} className="block">
                    {/* Image Container */}
                    <div className="relative overflow-hidden rounded-xl h-56 mb-5 shadow-md group-hover:shadow-xl transition-shadow duration-400">
                      <LazyBlogImage
                        postId={post.id}
                        hasImage={post.has_image}
                        title={post.title}
                        category={post.category}
                      />
                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className={`px-3 py-1.5 rounded-full text-white text-xs font-semibold tracking-wide ${catInfo.color} shadow-sm`}>
                          {catInfo.name}
                        </span>
                      </div>
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-[#264F77]/0 group-hover:bg-[#264F77]/10 transition-colors duration-400" />
                    </div>

                    {/* Content */}
                    <div className="px-1">
                      {/* Date */}
                      <div className="flex items-center gap-1.5 text-sm text-gray-400 mb-3">
                        <Calendar size={14} />
                        <time dateTime={post.published_at}>
                          {formatDate(post.published_at)}
                        </time>
                      </div>

                      {/* Title */}
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 leading-snug group-hover:text-[#264F77] transition-colors duration-300 line-clamp-2">
                        {post.title}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>

                      {/* Read More */}
                      <span className="inline-flex items-center gap-2 text-[#264F77] font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                        Mehr erfahren
                        <ArrowRight size={16} />
                      </span>
                    </div>
                  </Link>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
